<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Config } from '@/Config'
import * as Server from '@/services/Server'
import PageHeader from '@/components/PageHeader.vue'
import DateRange from '@/components/DateRange.vue'
import UserName from '@/components/UserName.vue'
import TrackTitle from '@/components/TrackTitle.vue'
import TrackDiagram from '@/components/TrackDiagram.vue'
import { podiumClass } from '@/lib/podium'
import { formatDecimal, formatSignedDecimal } from '@/lib/rating'
import { formatGap, gapsToPrevious } from '@/lib/times'
import { eventStateColor } from '@/lib/events'
import { useEventTitle } from '@/composables/useEventTitle'
import { useTrackTitle } from '@/composables/useTrackTitle'

/**
 * One track of an event: everybody's result on that track, in position order.
 * The positions come from the event ranking, so this table matches the per-track position shown
 * in the event detail.
 */
const route = useRoute()
const { t } = useI18n()
const { primaryName } = useTrackTitle()
const eventTitle = useEventTitle()

const eventId = computed(() => route.params.id)
const trackKey = computed(() => route.params.trackKey)

const data = ref(null)
const loading = ref(true)
const error = ref(false)

/** The event this track belongs to, as its own page names it. */
const eventName = computed(() => eventTitle(data.value?.event, `#${eventId.value}`))

/**
 * The distance of every player from the one above in the list, in the order the table shows them:
 * the first one has none, being the time the others are measured against.
 */
const previousGaps = computed(() =>
  gapsToPrevious(data.value?.standings ?? [], row => row.resultMs).map(formatGap)
)

const breadcrumbs = computed(() => [
  { title: t('events.breadcrumb'), to: { name: 'events' } },
  {
    title: eventName.value,
    to: { name: 'event_detail', params: { id: eventId.value } },
  },
  { title: data.value ? primaryName(data.value.track.trackKey) : trackKey.value },
])

const fetchTrack = () => {
  loading.value = true
  error.value = false

  Server.get(Config.server.routes.api.events_track(eventId.value, trackKey.value))
    .then(response => {
      data.value = response.data?.data ?? null
    })
    .catch(() => {
      error.value = true
      data.value = null
    })
    .finally(() => {
      loading.value = false
    })
}

onMounted(fetchTrack)
watch([eventId, trackKey], fetchTrack)
</script>

<template>
  <main>
    <v-container>
      <v-breadcrumbs :items="breadcrumbs" class="px-0 pb-2">
        <template v-slot:divider>
          <v-icon>mdi-chevron-right</v-icon>
        </template>
      </v-breadcrumbs>

      <div v-if="loading" class="d-flex justify-center py-12">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <v-alert v-else-if="error" type="error" variant="tonal">
        {{ $t('events.loadError') }}
      </v-alert>

      <template v-else-if="data">
        <!-- The track is the page: its standings take the wide side, while the event it belongs to
             and the track itself are read in the narrower column beside them. -->
        <PageHeader :title="primaryName(data.track.trackKey)" :subtitle="eventName" icon="mdi-flag-checkered">
          <template #title>
            <TrackTitle :track-id="data.track.trackKey" both-names />
          </template>
        </PageHeader>

        <v-row>
          <v-col cols="12" md="8" class="order-1">
            <v-card elevation="3" rounded="lg" class="overflow-hidden">
              <div class="d-flex align-center ga-3 pa-4">
                <v-avatar color="primary" variant="tonal" size="48">
                  <v-icon size="28">mdi-trophy-variant</v-icon>
                </v-avatar>
                <h2 class="text-h6 font-weight-bold">{{ $t('events.trackStandings') }}</h2>
              </div>

              <v-divider />

              <v-table v-if="data.standings.length" hover>
                <thead>
                  <tr>
                    <th style="width: 56px" class="text-center">#</th>
                    <th>{{ $t('events.player') }}</th>
                    <!-- The season is ordered by the score, so the score comes first: the column that
                         explains the order has to be the one you read first. -->
                    <template v-if="data.event.season">
                      <th class="text-center text-no-wrap">{{ $t('events.score') }}</th>
                      <th class="text-center">{{ $t('events.rating') }}</th>
                      <th class="text-center text-no-wrap">{{ $t('events.racesRun') }}</th>
                      <th class="text-center text-no-wrap">
                        {{ $t('events.rd') }}
                        <router-link
                          :to="{ name: 'info', hash: '#season-ranking' }"
                          class="header-help"
                          :title="$t('events.rdHelp')"
                          :aria-label="$t('events.rdHelp')"
                        >
                          <v-icon size="14" color="info">mdi-help-circle-outline</v-icon>
                        </router-link>
                      </th>
                    </template>
                    <th v-else class="text-center">{{ $t('events.time') }}</th>
                    <th v-if="!data.event.season" class="text-center text-no-wrap">{{ $t('standings.gap') }}</th>
                    <th v-if="data.event.pointsBased" class="text-center">{{ $t('events.points') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in data.standings" :key="row.userId" :class="{ 'me-row': row.isMe }">
                    <td class="text-center">
                      <v-icon v-if="row.position <= 3" :class="podiumClass(row.position)" size="20">mdi-medal</v-icon>
                      <span v-else class="text-medium-emphasis">{{ row.position }}</span>
                    </td>
                    <td class="font-weight-medium"><UserName :name="row.playerName" :crest="row.crest" /></td>
                    <!-- The score leads, because it is what the order is built on. The rating follows:
                         it can differ from the score, and seeing both shows why the order is this one.
                         One decimal each: rounded to the unit, 23.7 and 24.1 would both read 24. -->
                    <template v-if="data.event.season">
                      <td class="text-center text-mono font-weight-bold">{{ formatSignedDecimal(row.score) }}</td>
                      <td class="text-center text-mono text-medium-emphasis">{{ formatSignedDecimal(row.rating) }}</td>
                      <td class="text-center text-mono text-medium-emphasis">{{ row.races ?? '–' }}</td>
                      <td class="text-center text-mono text-medium-emphasis">{{ formatDecimal(row.rd) }}</td>
                    </template>
                    <td v-else class="text-center text-mono">{{ row.resultTime ?? '–' }}</td>
                    <td v-if="!data.event.season" class="text-center text-mono text-medium-emphasis">
                      {{ previousGaps[index] ?? '' }}
                    </td>
                    <td v-if="data.event.pointsBased" class="text-center font-weight-bold">
                      {{ row.points ?? '–' }}
                    </td>
                  </tr>
                </tbody>
              </v-table>

              <div v-else class="d-flex align-center ga-2 pa-4 text-medium-emphasis">
                <v-icon size="20">mdi-flag-outline</v-icon>
                <span class="text-caption">{{ $t('events.noTrackResults') }}</span>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="4" class="order-2 d-flex flex-column ga-4">
            <v-card elevation="3" rounded="lg" class="overflow-hidden">
              <div class="d-flex align-center ga-3 pa-4">
                <v-avatar color="primary" variant="tonal" size="48">
                  <v-icon size="28">mdi-calendar-star</v-icon>
                </v-avatar>
                <h2 class="text-h6 font-weight-bold">{{ $t('events.eventInfo') }}</h2>
              </div>

              <v-divider />

              <div class="pa-4">
                <div class="d-flex flex-wrap align-center ga-2">
                  <span class="text-body-1 font-weight-medium">{{ eventName }}</span>
                  <v-chip :color="eventStateColor(data.event.state)" size="small" variant="tonal" label>
                    {{ $t(`eventStates.${data.event.state}`) }}
                  </v-chip>
                </div>

                <p class="d-flex align-center ga-1 text-caption text-medium-emphasis mt-3 mb-0">
                  <v-icon size="16">mdi-calendar-range</v-icon>
                  <DateRange :from="data.event.dateStart" :to="data.event.dateEnd" />
                </p>

                <p
                  v-if="data.event.usesLaps"
                  class="d-flex align-center ga-1 text-caption text-medium-emphasis mt-1 mb-0"
                >
                  <v-icon size="16">mdi-counter</v-icon>
                  <span>{{ $t('events.laps') }}: {{ data.track.laps }}</span>
                </p>

                <v-btn
                  :to="{ name: 'event_detail', params: { id: eventId } }"
                  variant="tonal"
                  class="mt-4 w-100"
                >
                  {{ $t('events.openEvent') }}
                </v-btn>
              </div>
            </v-card>

            <!-- The track the event is raced on: its drawing opens the track's own page -->
            <v-card
              :to="{ name: 'track', params: { trackId: data.track.trackKey } }"
              hover
              elevation="3"
              rounded="lg"
              class="overflow-hidden"
            >
              <div class="d-flex align-center ga-3 pa-4">
                <v-avatar color="primary" variant="tonal" size="48">
                  <v-icon size="28">mdi-map-marker-path</v-icon>
                </v-avatar>
                <h2 class="text-h6 font-weight-bold">{{ $t('events.track') }}</h2>
              </div>

              <v-divider />

              <div class="pa-4">
                <TrackTitle :track-id="data.track.trackKey" variant="stacked" />
                <TrackDiagram :track-id="data.track.trackKey" class="mt-3" />
              </div>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </v-container>
  </main>
</template>

<style scoped>
.me-row td {
  background-color: rgba(var(--v-theme-primary), 0.06);
}

/* The little "?" of a season column: it links to the page that explains the model. */
.header-help {
  display: inline-flex;
  vertical-align: middle;
  margin-left: 2px;
  text-decoration: none;
}
</style>
